import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  TextField,
  Typography,
  Box,
  FormHelperText,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
// import { format } from 'date-fns'; // Not needed
import { nb } from 'date-fns/locale';
import { COUNTRIES, PHONE_CODES } from '../../constants';
import { RegistrationConfig, DEFAULT_REGISTRATION_CONFIG } from '../../contexts/EventEditionContext';
import LanguagePreference from './LanguagePreference';

interface PersonalInfoFormProps {
  formData: {
    firstName: string;
    lastName: string;
    dateOfBirth: Date | null;
    nationality: string;
    email: string;
    phoneCountryCode: string;
    phoneNumber: string;
    representing: string;
    languagePreference?: 'en' | 'no' | '';
  };
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
  fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>>;
  onBlur?: (field: string) => void;
  isEditingExisting?: boolean;
  isFull?: boolean;
  isEmailReadOnly?: boolean;
  registrationConfig?: RegistrationConfig;
  representingOptions?: string[];
}

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({ formData, onChange, errors, fieldRefs, onBlur, isEmailReadOnly, registrationConfig, representingOptions }) => {
  const { t } = useTranslation();

  // Get registration config with defaults
  const config: RegistrationConfig = {
    fields: {
      ...DEFAULT_REGISTRATION_CONFIG.fields,
      ...registrationConfig?.fields
    }
  };

  // Effect to scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Find selected country and phone code
  const selectedPhoneCode = PHONE_CODES.find(pc => pc.code === formData.phoneCountryCode) || PHONE_CODES[0];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns as any} adapterLocale={nb}>
      <Box sx={{ mt: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('form.personalInfo')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('form.personalInfoDesc')}
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="firstName"
              name="firstName"
              label={t('form.firstName')}
              fullWidth
              variant="outlined"
              value={formData.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
              inputProps={{ maxLength: 60 }}
              onBlur={() => onBlur && onBlur('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName || ''}
              inputRef={fieldRefs.firstName as any}
              autoFocus
              sx={{
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="lastName"
              name="lastName"
              label={t('form.lastName')}
              fullWidth
              variant="outlined"
              value={formData.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
              inputProps={{ maxLength: 60 }}
              onBlur={() => onBlur && onBlur('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName || ''}
              inputRef={fieldRefs.lastName as any}
              sx={{
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <DatePicker
              label={t('form.dateOfBirth')}
              value={formData.dateOfBirth}
              onChange={(date) => {
                // Update the form data with the new date
                onChange('dateOfBirth', date);
                
                // Only validate immediately for complete dates from the picker
                // (not during manual typing)
                if (date instanceof Date && !isNaN(date.getTime()) && 
                    date.getFullYear() > 1900 && date.getFullYear() <= new Date().getFullYear()) {
                  setTimeout(() => {
                    onBlur && onBlur('dateOfBirth');
                  }, 100);
                }
              }}
              // Validate when the picker closes
              onClose={() => onBlur && onBlur('dateOfBirth')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  required
                  error={!!errors.dateOfBirth}
                  helperText={errors.dateOfBirth || ''}
                  inputRef={fieldRefs.dateOfBirth}
                  // Add onBlur handler to the input field for manual typing validation
                  onBlur={() => onBlur && onBlur('dateOfBirth')}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    },
                    '& .MuiIconButton-root': {
                      color: 'text.primary',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'text.primary',
                    }
                  }}
                />
              )}
              // Norwegian date format
              // Using Norwegian date format
              // formatDensity="spacious" removed as it's not supported in this version
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Autocomplete
              id="nationality-autocomplete"
              options={COUNTRIES}
              getOptionLabel={(option) => option.name}
              groupBy={(option) => option.isCommon ? t('form.commonCountries') : t('form.allCountries')}
              value={COUNTRIES.find(country => country.code === formData.nationality) || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  onChange('nationality', newValue.code);
                }
              }}
              onBlur={() => onBlur && onBlur('nationality')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('form.nationality')}
                  variant="outlined"
                  helperText={errors.nationality || t('form.nationalityHelper')}
                  error={!!errors.nationality}
                  inputRef={fieldRefs.nationality}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            {isEmailReadOnly ? (
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('form.email')}
                </Typography>
                <Typography
  variant="body1"
  sx={theme => ({
    px: 1,
    py: 1,
    bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
    color: theme.palette.text.primary,
    borderRadius: 1,
    wordBreak: 'break-all',
    fontFamily: 'monospace',
    fontSize: '1rem',
    fontWeight: 500
  })}
  tabIndex={-1}
>
  {formData.email}
</Typography>
                <FormHelperText sx={{ ml: 0 }}>{errors.email || t('form.emailHelper')}</FormHelperText>
              </Box>
            ) : (
              <TextField
                required
                id="email"
                name="email"
                label={t('form.email')}
                fullWidth
                variant="outlined"
                type="email"
                value={formData.email}
                onChange={(e) => onChange('email', e.target.value)}
                inputProps={{ maxLength: 60 }}
                onBlur={() => onBlur && onBlur('email')}
                error={!!errors.email}
                helperText={errors.email || t('form.emailHelper')}
                inputRef={fieldRefs.email as any}
                sx={{
                  '& .MuiInputBase-input': {
                    color: 'text.primary',
                  }
                }}
              />
            )}
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Autocomplete
              id="phone-code-autocomplete"
              options={PHONE_CODES}
              getOptionLabel={(option) => `${option.flag} ${option.code} (${option.country})`}
              groupBy={(option) => option.isCommon ? t('form.commonCountries') : t('form.allCountries')}
              value={selectedPhoneCode}
              onChange={(_, newValue) => {
                if (newValue) {
                  onChange('phoneCountryCode', newValue.code);
                }
              }}
              onBlur={() => onBlur && onBlur('phoneCountryCode')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('form.countryCode')}
                  variant="outlined"
                  error={!!errors.phoneCountryCode}
                  helperText={errors.phoneCountryCode || ''}
                  inputRef={fieldRefs.phoneCountryCode}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: 'text.primary',
                    }
                  }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              required
              id="phoneNumber"
              name="phoneNumber"
              label={t('form.phoneNumber')}
              fullWidth
              variant="outlined"
              value={formData.phoneNumber}
              onChange={(e) => onChange('phoneNumber', e.target.value.replace(/[^0-9+\-\s()]/g, ''))}
              onBlur={() => onBlur && onBlur('phoneNumber')}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber || t('form.phoneHelper')}
              inputRef={fieldRefs.phoneNumber as any}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {selectedPhoneCode.code}
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                }
              }}
            />
          </Grid>
          
          {/* Representing field - only show if configured */}
          {config.fields.representing && (
            <Grid item xs={12}>
              <Autocomplete
                id="representing-autocomplete"
                freeSolo
                options={(representingOptions || []).filter(Boolean)}
                value={formData.representing || ''}
                inputValue={formData.representing || ''}
                onChange={(_, newValue) => {
                  const v = typeof newValue === 'string' ? newValue : (newValue ?? '');
                  onChange('representing', v);
                }}
                onInputChange={(_, newInputValue) => {
                  onChange('representing', newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="representing"
                    name="representing"
                    label={t('form.representing')}
                    fullWidth
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                      maxLength: 60,
                    }}
                    helperText={t('form.representingHelper')}
                  />
                )}
              />
            </Grid>
          )}
          
          {/* Language Preference field */}
          <Grid item xs={12}>
            <LanguagePreference
              value={formData.languagePreference || ''}
              onChange={(value) => onChange('languagePreference', value)}
              error={errors.languagePreference}
              required={config.fields.languagePreference?.required}
            />
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default PersonalInfoForm;
